import React, { useState, useEffect } from 'react';
import { observer, useLocalObservable } from 'mobx-react-lite';
import AdminStore from '../Stores/AdminStore';
import './RolesAndPermissionsPage.css';

// Модальное окно для создания роли
const RoleForm = ({ onClose, adminStore }) => {
  const [roleName, setRoleName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await adminStore.createRole(roleName);
      onClose();
    } catch (err) {
      setError(err.message || "Ошибка при создании роли");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <h2>Создать роль</h2>
        {error && <div className="modal-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <input 
            type="text" 
            placeholder="Название роли" 
            value={roleName} 
            onChange={(e) => setRoleName(e.target.value)} 
            className="modal-input"
          />
          <div className="modal-buttons">
            <button type="submit" disabled={loading} className="modal-btn modal-save-btn">
              {loading ? 'Создание...' : 'Создать'}
            </button>
            <button type="button" onClick={onClose} className="modal-btn modal-cancel-btn">
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Модальное окно для создания права
const PermissionForm = ({ onClose, adminStore }) => {
  const [permissionName, setPermissionName] = useState('');
  const [permissionCode, setPermissionCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await adminStore.createPermission({ name: permissionName, code: permissionCode });
      onClose();
    } catch (err) {
      setError(err.message || "Ошибка при создании права");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <h2>Создать право</h2>
        {error && <div className="modal-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <input 
            type="text" 
            placeholder="Название права" 
            value={permissionName} 
            onChange={(e) => setPermissionName(e.target.value)} 
            className="modal-input"
          />
          <input 
            type="text" 
            placeholder="Код права" 
            value={permissionCode} 
            onChange={(e) => setPermissionCode(e.target.value)} 
            className="modal-input"
          />
          <div className="modal-buttons">
            <button type="submit" disabled={loading} className="modal-btn modal-save-btn">
              {loading ? 'Создание...' : 'Создать'}
            </button>
            <button type="button" onClick={onClose} className="modal-btn modal-cancel-btn">
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const RolesAndPermissionsPage = observer(({ adminStore }) => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [showPermissionForm, setShowPermissionForm] = useState(false);
  const [assignedPermissions, setAssignedPermissions] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    adminStore.fetchRolesAndPermissions();
  }, []);
  // Загружаем роли и права из стора при монтировании
  useEffect(() => {
    adminStore.fetchRolesAndPermissions();
  }, []);

  // При выборе роли используем данные из стора
  useEffect(() => {
    if (!selectedRole) {
      setAssignedPermissions([]);
      setAvailablePermissions([]);
      return;
    }
    const { assigned, available } = adminStore.updateRolePermissions(selectedRole.id);
    setAssignedPermissions(assigned);
    setAvailablePermissions(available);
    setLocalError(null);
  }, [selectedRole, adminStore.roles, adminStore.permissions]);

  const handleAddPermission = async (permissionId) => {
    try {
      await adminStore.addPermissionToRole(selectedRole.id, permissionId);
      // Обновляем данные после добавления
      const { assigned, available } = adminStore.updateRolePermissions(selectedRole.id);
      setAssignedPermissions(assigned);
      setAvailablePermissions(available);
    } catch (err) {
      setLocalError(err.message || 'Ошибка при добавлении права');
    }
  };

  const handleRemovePermission = async (permissionId) => {
    try {
      await adminStore.removePermissionFromRole(selectedRole.id, permissionId);
      const { assigned, available } = adminStore.updateRolePermissions(selectedRole.id);
      setAssignedPermissions(assigned);
      setAvailablePermissions(available);
    } catch (err) {
      setLocalError(err.message || 'Ошибка при удалении права');
    }
  };

  const renderRolesList = () => {
    return (
      <ul className="roles-list">
        {adminStore.roles.map(role => (
          <li
            key={role.id}
            className={`role-item ${selectedRole && selectedRole.id === role.id ? 'active' : ''}`}
            onClick={() => setSelectedRole(role)}
          >
            {role.name}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="roles-permissions-page">
      <h1>Управление ролями и правами</h1>
      {localError && <div className="error-message">{localError}</div>}
      <div className="content-wrapper">
        <aside className="roles-sidebar">
          <h2>Роли</h2>
          {adminStore.loadingRoles ? (
            <p>Загрузка ролей...</p>
          ) : (
            renderRolesList()
          )}
          <button className="create-button" onClick={() => setShowRoleForm(true)}>
            Создать роль
          </button>
        </aside>
        <main className="permissions-main">
          {selectedRole ? (
            <>
              <h2>Права для роли: {selectedRole.name}</h2>
              <div className="dual-list-container">
                <div className="list-column">
                  <h3>Доступные права</h3>
                  <ul className="permissions-list">
                    {availablePermissions.map(permission => (
                      <li 
                        key={permission.id} 
                        className="permission-item"
                        style={ permission.target === 'client' ? { backgroundColor: 'rgb(0,123,255)', color: 'white' } : {} }
                      >
                        <span>{permission.description}</span>
                        <button onClick={() => handleAddPermission(permission.id)}>
                          Добавить
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="list-column">
                  <h3>Назначенные права</h3>
                  <ul className="permissions-list">
                    {assignedPermissions.map(permission => (
                      <li 
                        key={permission.id} 
                        className="permission-item"
                        style={ permission.target === 'client' ? { backgroundColor: 'rgb(0,123,255)', color: 'white' } : {} }
                      >
                        <span>{permission.description}</span>
                        <button onClick={() => handleRemovePermission(permission.id)}>
                          Удалить
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <button className="create-button" onClick={() => setShowPermissionForm(true)}>
                Создать право
              </button>
            </>
          ) : (
            <p>Выберите роль для управления правами</p>
          )}
        </main>
      </div>
      {showRoleForm && <RoleForm onClose={() => setShowRoleForm(false)} adminStore={adminStore} />}
      {showPermissionForm && <PermissionForm onClose={() => setShowPermissionForm(false)} adminStore={adminStore} />}

    </div>
  );
});

export default RolesAndPermissionsPage;
